import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { useAuthStore } from "./store/authStore";
import { isHesapSubdomain, isLegacyHesapDomain } from "./lib/subdomain";
import { DomainMovedNotice } from "./components/DomainMovedNotice";
import { useHesapBranding } from "./store/hesapBrandingStore";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import CustomersPage from "./pages/CustomersPage";
import CustomerDetailPage from "./pages/CustomerDetailPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import ShootsPage from "./pages/ShootsPage";
import CalendarPage from "./pages/CalendarPage";
import PackagesPage from "./pages/PackagesPage";
import TeamPage from "./pages/TeamPage";
import VenuesPage from "./pages/VenuesPage";
import NotificationsPage from "./pages/NotificationsPage";
import SettingsPage from "./pages/SettingsPage";
import AlbumPage from "./pages/AlbumPage";
import GalleryPage from "./pages/GalleryPage";
import AlbumTemplatesPage from "./pages/AlbumTemplatesPage";

// Salon Pages
import SalonDashboardPage from "./pages/salon/SalonDashboardPage";
import SalonCustomersPage from "./pages/salon/SalonCustomersPage";
import SalonCustomerDetailPage from "./pages/salon/SalonCustomerDetailPage";
import SalonNewCustomerPage from "./pages/salon/SalonNewCustomerPage";
import SalonPackagesPage from "./pages/salon/SalonPackagesPage";
import SalonSettingsPage from "./pages/salon/SalonSettingsPage";
import SalonCalendarPage from "./pages/salon/SalonCalendarPage";

// Admin Pages
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminTemplatesPage from "./pages/admin/AdminTemplatesPage";
import AdminPlansPage from "./pages/admin/AdminPlansPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AdminAlbumCompaniesPage from "./pages/admin/AdminAlbumCompaniesPage";

// AkıllıÇözüm Pages
import HesapLoginPage from "./pages/hesap/HesapLoginPage";
import HesapRegisterPage from "./pages/hesap/HesapRegisterPage";
import HesapDashboardPage from "./pages/hesap/HesapDashboardPage";
import HesapProductsPage from "./pages/hesap/HesapProductsPage";
import HesapStockPage from "./pages/hesap/HesapStockPage";
import HesapFiyatGorPage from "./pages/hesap/HesapFiyatGorPage";
import HesapPOSPage from "./pages/hesap/HesapPOSPage";
import HesapCustomersPage from "./pages/hesap/HesapCustomersPage";
import HesapFinancePage from "./pages/hesap/HesapFinancePage";
import HesapUsersPage from "./pages/hesap/HesapUsersPage";
import HesapApartmentPage from "./pages/hesap/HesapApartmentPage";
import HesapSolarPage from "./pages/hesap/HesapSolarPage";
import HesapSmartPage from "./pages/hesap/HesapSmartPage";
import HesapYemekSiparisPage from "./pages/hesap/HesapYemekSiparisPage";
import HesapYemekMutfakPage from "./pages/hesap/HesapYemekMutfakPage";
import HesapSalesReportPage from "./pages/hesap/HesapSalesReportPage";
import HesapWorkTrackingPage from "./pages/hesap/HesapWorkTrackingPage";
import HesapTeknikServisPage from "./pages/hesap/HesapTeknikServisPage";
import HesapCloudPage from "./pages/hesap/HesapCloudPage";
import HesapSettingsPage from "./pages/hesap/HesapSettingsPage";
import HesapSiteSettingsPage from "./pages/hesap/HesapSiteSettingsPage";
import HesapLandingPage from "./pages/hesap/HesapLandingPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import PublicTeklifPage from "./pages/PublicTeklifPage";
import PublicVitrinPage from "./pages/PublicVitrinPage";
import HesapVitrinPage from "./pages/hesap/HesapVitrinPage";
import AdminStoragePage from "./pages/admin/AdminStoragePage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import HesapAdminSubscriptionsPage from "./pages/hesap/HesapAdminSubscriptionsPage";
import HesapTeklifPage from "./pages/hesap/HesapTeklifPage";
import HesapHesaplamaPage from "./pages/hesap/HesapHesaplamaPage";
import PublicCloudSharePage from "./pages/PublicCloudSharePage";
import PublicCihazPage from "./pages/PublicCihazPage";
import HesapLayout from "./components/hesap/HesapLayout";
import { useHesapAuth } from "./store/hesapStore";

// Layout
import DashboardLayout from "./components/layout/DashboardLayout";
import AdminLayout from "./components/layout/AdminLayout";
import SalonLayout from "./components/layout/SalonLayout";
import AlbumcuLayout from "./components/layout/AlbumcuLayout";

// Print Orders / Albümcü
import PrintOrdersPage from "./pages/PrintOrdersPage";
import AlbumcuDashboard from "./pages/albumcu/AlbumcuDashboard";
import AlbumcuProductsPage from "./pages/albumcu/AlbumcuProductsPage";
import AlbumcuOrdersPage from "./pages/albumcu/AlbumcuOrdersPage";

// PWA install prompt
import PWAInstallPrompt from "./components/PWAInstallPrompt";

// Subscription
import SubscriptionPage from "./pages/SubscriptionPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/giris" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/giris" replace />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/panel" replace />;
  }
  
  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  
  if (isAuthenticated) {
    // Admin ise admin paneline yönlendir
    if (user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/panel" replace />;
  }
  
  return children;
};

// AkıllıÇözüm Protected Route
const HesapRoute = ({ children }) => {
  const { token } = useHesapAuth();
  const loginPath = isHesapSubdomain() ? '/giris' : '/hesap/giris';
  if (!token) {
    if (isHesapSubdomain() && window.location.pathname === '/') return <HesapLandingPage />;
    return <Navigate to={loginPath} replace />;
  }
  return children;
};

function App() {
  const { user } = useAuthStore();
  const hesapMode = isHesapSubdomain();

  // Apply theme on mount and changes
  useEffect(() => {
    const theme = user?.theme || 'light';
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [user?.theme]);

  // Sayfa açıldığında + odaklandığında token expire kontrolü.
  // Bu sayede kullanıcı 2 gün sonra dönerse eski token temizlenip login'e yönlenir.
  useEffect(() => {
    const checkAuth = () => {
      try {
        useAuthStore.getState().checkAuthValid?.();
        useHesapAuth.getState().checkAuthValid?.();
      } catch { /* noop */ }
    };
    checkAuth();
    window.addEventListener('focus', checkAuth);
    document.addEventListener('visibilitychange', checkAuth);
    // Her 5 dakikada bir de kontrol (uzun süreli açık sekmeler için)
    const interval = setInterval(checkAuth, 5 * 60 * 1000);
    return () => {
      window.removeEventListener('focus', checkAuth);
      document.removeEventListener('visibilitychange', checkAuth);
      clearInterval(interval);
    };
  }, []);

  // ⏱️ Idle logout — 1 saat işlem yapılmazsa otomatik çıkış.
  // Kullanıcı istediği için: "1 saat boyunca işlem yapmazsa kapatsın".
  useEffect(() => {
    const IDLE_MS = 60 * 60 * 1000; // 60 dakika
    let timer = null;
    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          const wt = useAuthStore.getState();
          const ht = useHesapAuth.getState();
          const wasWt = wt.isAuthenticated;
          const wasHt = !!ht.token;
          if (wasWt) wt.logout();
          if (wasHt) ht.logout();
          if (wasWt || wasHt) {
            try {
              toast.info('1 saat işlem yapılmadığı için oturumunuz kapatıldı. Tekrar giriş yapın.');
            } catch { /* noop */ }
          }
        } catch { /* noop */ }
      }, IDLE_MS);
    };
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      if (timer) clearTimeout(timer);
      events.forEach(ev => window.removeEventListener(ev, resetTimer));
    };
  }, []);

  useEffect(() => {
    if (hesapMode) useHesapBranding.getState().load();
  }, [hesapMode]);

  // Eski adres (hesap.weddingtakip.com) → yeni adrese yönlendirme bildirimi
  if (isLegacyHesapDomain()) return <DomainMovedNotice />;

  // AkıllıÇözüm modu (www.akillicozum.com.tr)
  if (hesapMode) {
    return (
      <>
        <Toaster position="top-right" richColors toastOptions={{ style: { fontFamily: 'Outfit, sans-serif' } }} />
        <BrowserRouter>
          <Routes>
            <Route path="/cihaz/:id" element={<PublicCihazPage />} />
          <Route path="/teklif/:token" element={<PublicTeklifPage />} />
          <Route path="/vitrin/:tenantId" element={<PublicVitrinPage />} />
            <Route path="/teklif/:token" element={<PublicTeklifPage />} />
            <Route path="/vitrin/:tenantId" element={<PublicVitrinPage />} />
            <Route path="/bulut/paylasim/:token" element={<PublicCloudSharePage />} />
            <Route path="/giris" element={<HesapLoginPage />} />
            <Route path="/kayit" element={<HesapRegisterPage />} />
            <Route path="/sifremi-unuttum" element={<ForgotPasswordPage apiBase="/api/hesap/auth" loginPath="/giris" variant="hesap" />} />
            <Route path="/sifre-sifirla" element={<ResetPasswordPage apiBase="/api/hesap/auth" loginPath="/giris" variant="hesap" />} />
            <Route path="/eposta-onay" element={<VerifyEmailPage apiBase="/api/hesap/auth" loginPath="/giris" variant="hesap" />} />
            <Route
              path="/"
              element={
                <HesapRoute>
                  <HesapLayout />
                </HesapRoute>
              }
            >
              <Route index element={<HesapDashboardPage />} />
              <Route path="satis" element={<HesapPOSPage />} />
              <Route path="satis-rapor" element={<HesapSalesReportPage />} />
              <Route path="is-takibi" element={<HesapWorkTrackingPage />} />
              <Route path="teknik-servis" element={<HesapTeknikServisPage />} />
              <Route path="vitrin-yonetim" element={<HesapVitrinPage />} />
              <Route path="bulut" element={<HesapCloudPage />} />
              <Route path="urunler" element={<HesapProductsPage />} />
              <Route path="stok" element={<HesapStockPage />} />
              <Route path="fiyat-gor" element={<HesapFiyatGorPage />} />
              <Route path="teklif" element={<HesapTeklifPage />} />
              <Route path="hesaplama" element={<HesapHesaplamaPage />} />
              <Route path="musteriler" element={<HesapCustomersPage />} />
              <Route path="gelir-gider" element={<HesapFinancePage />} />
              <Route path="apartman" element={<HesapApartmentPage />} />
              <Route path="solar" element={<HesapSolarPage />} />
              <Route path="akilli" element={<HesapSmartPage />} />
              <Route path="yemek-siparis" element={<HesapYemekSiparisPage />} />
              <Route path="yemek-mutfak" element={<HesapYemekMutfakPage />} />
              <Route path="kullanicilar" element={<HesapUsersPage />} />
              <Route path="ayarlar" element={<HesapSettingsPage />} />
              <Route path="site-ayarlari" element={<HesapSiteSettingsPage />} />
              <Route path="abonelikler" element={<HesapAdminSubscriptionsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </>
    );
  }

  // Ana domain modunda (weddingtakip.com)
  return (
    <>
      <Toaster 
        position="top-right" 
        richColors 
        toastOptions={{
          style: {
            fontFamily: 'Outfit, sans-serif',
          },
        }}
      />
      <PWAInstallPrompt />
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route 
            path="/giris" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/kayit" 
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } 
          />
          
          <Route path="/sifremi-unuttum" element={<ForgotPasswordPage apiBase="/api/auth" loginPath="/giris" variant="wedding" />} />
          <Route path="/sifre-sifirla" element={<ResetPasswordPage apiBase="/api/auth" loginPath="/giris" variant="wedding" />} />
          <Route path="/eposta-onay" element={<VerifyEmailPage apiBase="/api/auth" loginPath="/giris" variant="wedding" />} />

          {/* Public Gallery - Customer Access */}
          <Route path="/galeri/:token" element={<GalleryPage />} />

          {/* Public Cihaz Share - Herkese Acik */}
          <Route path="/cihaz/:id" element={<PublicCihazPage />} />
          {/* Public Cloud Share */}
          <Route path="/bulut/paylasim/:token" element={<PublicCloudSharePage />} />
          
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="kullanicilar" element={<AdminUsersPage />} />
            <Route path="sablonlar" element={<AdminTemplatesPage />} />
            <Route path="album-firmalari" element={<AdminAlbumCompaniesPage />} />
            <Route path="paketler" element={<AdminPlansPage />} />
            <Route path="ayarlar" element={<AdminSettingsPage />} />
            <Route path="alan-kullanimi" element={<AdminStoragePage />} />
          </Route>
          
          {/* Protected Dashboard Routes */}
          <Route
            path="/panel"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="musteriler" element={<CustomersPage />} />
            <Route path="musteri/:customerId" element={<CustomerDetailPage />} />
            <Route path="randevular" element={<AppointmentsPage />} />
            <Route path="cekimler" element={<ShootsPage />} />
            <Route path="takvim" element={<CalendarPage />} />
            <Route path="paketler" element={<PackagesPage />} />
            <Route path="ekip" element={<TeamPage />} />
            <Route path="mekanlar" element={<VenuesPage />} />
            <Route path="album-sablonlari" element={<AlbumTemplatesPage />} />
            <Route path="baski-siparisleri" element={<PrintOrdersPage />} />
            <Route path="abonelik" element={<SubscriptionPage />} />
            <Route path="bildirimler" element={<NotificationsPage />} />
            <Route path="ayarlar" element={<SettingsPage />} />
            <Route path="album/:customerId" element={<AlbumPage />} />
          </Route>

          {/* Salon Panel Routes */}
          <Route
            path="/salon"
            element={
              <ProtectedRoute>
                <SalonLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<SalonDashboardPage />} />
            <Route path="musteriler/yeni" element={<SalonNewCustomerPage />} />
            <Route path="musteriler/:customerId" element={<SalonCustomerDetailPage />} />
            <Route path="musteriler" element={<SalonCustomersPage />} />
            <Route path="takvim" element={<SalonCalendarPage />} />
            <Route path="paketler" element={<SalonPackagesPage />} />
            <Route path="bildirimler" element={<NotificationsPage />} />
            <Route path="ayarlar" element={<SalonSettingsPage />} />
          </Route>

          {/* Albümcü Panel Routes */}
          <Route
            path="/albumcu"
            element={
              <ProtectedRoute>
                <AlbumcuLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AlbumcuDashboard />} />
            <Route path="urunler" element={<AlbumcuProductsPage />} />
            <Route path="siparisler" element={<AlbumcuOrdersPage />} />
            <Route path="ayarlar" element={<SettingsPage />} />
          </Route>

          {/* AkıllıÇözüm Routes */}
          <Route path="/hesap/giris" element={<HesapLoginPage />} />
          <Route path="/hesap/kayit" element={<HesapRegisterPage />} />
          <Route path="/hesap/sifremi-unuttum" element={<ForgotPasswordPage apiBase="/api/hesap/auth" loginPath="/hesap/giris" variant="hesap" />} />
          <Route path="/hesap/sifre-sifirla" element={<ResetPasswordPage apiBase="/api/hesap/auth" loginPath="/hesap/giris" variant="hesap" />} />
          <Route path="/hesap/eposta-onay" element={<VerifyEmailPage apiBase="/api/hesap/auth" loginPath="/hesap/giris" variant="hesap" />} />
          <Route
            path="/hesap"
            element={
              <HesapRoute>
                <HesapLayout />
              </HesapRoute>
            }
          >
            <Route index element={<HesapDashboardPage />} />
            <Route path="satis" element={<HesapPOSPage />} />
            <Route path="satis-rapor" element={<HesapSalesReportPage />} />
            <Route path="is-takibi" element={<HesapWorkTrackingPage />} />
            <Route path="teknik-servis" element={<HesapTeknikServisPage />} />
            <Route path="vitrin-yonetim" element={<HesapVitrinPage />} />
            <Route path="bulut" element={<HesapCloudPage />} />
            <Route path="urunler" element={<HesapProductsPage />} />
            <Route path="stok" element={<HesapStockPage />} />
            <Route path="fiyat-gor" element={<HesapFiyatGorPage />} />
            <Route path="teklif" element={<HesapTeklifPage />} />
            <Route path="hesaplama" element={<HesapHesaplamaPage />} />
            <Route path="musteriler" element={<HesapCustomersPage />} />
            <Route path="gelir-gider" element={<HesapFinancePage />} />
            <Route path="apartman" element={<HesapApartmentPage />} />
            <Route path="solar" element={<HesapSolarPage />} />
            <Route path="akilli" element={<HesapSmartPage />} />
            <Route path="yemek-siparis" element={<HesapYemekSiparisPage />} />
            <Route path="yemek-mutfak" element={<HesapYemekMutfakPage />} />
            <Route path="kullanicilar" element={<HesapUsersPage />} />
            <Route path="ayarlar" element={<HesapSettingsPage />} />
              <Route path="site-ayarlari" element={<HesapSiteSettingsPage />} />
            <Route path="abonelikler" element={<HesapAdminSubscriptionsPage />} />
          </Route>

          {/* 404 Redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;
